/**
 * JIRA Notification Service
 * Creates JIRA support tickets for account creation and instance access requests.
 * Credentials are handled server-side via the proxy — never exposed to the browser.
 */
class JiraNotificationService {
  constructor() {
    this.isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    this.proxyUrl = this.isProduction ? '/api/jira/create-issue' : 'http://localhost:3001/api/jira/create-issue';
    console.log('🎫 [JiraService] Initialized');
  }

  /**
   * Create a JIRA task ticket via the server-side proxy
   */
  async createJiraTicket(payload) {
    try {
      const response = await fetch(this.proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log(`✅ [JiraService] Ticket created: ${data.issueKey}`);
        return { success: true, issueKey: data.issueKey, message: `JIRA ticket ${data.issueKey} created successfully` };
      } else {
        throw new Error(data.message || `Proxy responded with status ${response.status}`);
      }
    } catch (error) {
      console.error('❌ [JiraService] Error creating ticket:', error.message);
      return { success: false, message: `Failed to create JIRA ticket: ${error.message}`, error: error.message };
    }
  }

  /**
   * Send account creation notification — creates a JIRA task ticket
   */
  async sendAccountCreationNotification(userDetails) {
    if (!userDetails.email) {
      console.error('❌ [JiraService] No email found in account creation form — ticket not created');
      return { success: false, message: 'Unable to create support ticket: email address is required.' };
    }
    console.log('📧 [JiraService] Creating account creation ticket...');
    const payload = this.buildAccountCreationPayload(userDetails);
    return this.createJiraTicket(payload);
  }

  /**
   * Send instance access request notification — creates a JIRA task ticket
   */
  async sendInstanceAccessRequestNotification(requestDetails) {
    const reporterEmail = requestDetails.userEmail || requestDetails.email;
    if (!reporterEmail) {
      console.error('❌ [JiraService] No logged-in user email found — ticket not created');
      return { success: false, message: 'Unable to create support ticket: no logged-in user email found.' };
    }
    console.log('📧 [JiraService] Creating instance access request ticket...');
    const payload = this.buildInstanceAccessPayload(requestDetails);
    return this.createJiraTicket(payload);
  }

  /**
   * Build JIRA Service Desk payload for account creation request
   * API: POST /rest/servicedeskapi/request
   *
   * Field mapping:
   *   customfield_16130 → Full Name
   *   customfield_16131 → Email ID
   *   customfield_16132 → Company
   *   customfield_16133 → Purpose
   *   customfield_16134 → Specific Use Case
   *   customfield_16135 → Action Required
   *   customfield_10145 → Environment  (mandatory)
   */
  buildAccountCreationPayload(userDetails) {
    const fullName = `${userDetails.firstName} ${userDetails.lastName}`.trim();
    const environment = this.isProduction ? 'Production' : 'Development';

    return {
      serviceDeskId: '2',
      requestTypeId: '12',
      requestFieldValues: {
        summary: `Account Creation Request - ${fullName} (${userDetails.email})`,
        description: `Account creation request submitted by ${fullName} from ${userDetails.companyName || 'N/A'}.`,
        priority: { name: 'P3' },
        customfield_16130: fullName,
        customfield_16131: userDetails.email,
        customfield_16132: userDetails.companyName || 'Not specified',
        customfield_16133: userDetails.primaryUseCase || 'Not specified',
        customfield_16134: userDetails.reasonForUse || 'Not specified',
        customfield_16135: 'Review and approve account creation request',
        customfield_10145: { value: environment }
      }
    };
  }

  /**
   * Build JIRA Service Desk payload for instance access request
   * API: POST /rest/servicedeskapi/request
   *
   * Field mapping:
   *   customfield_16130 → Full Name
   *   customfield_16131 → Email ID
   *   customfield_16132 → Company
   *   customfield_16135 → Action Required
   *   customfield_16136 → Instance
   *   customfield_16137 → Reason for Access
   *   customfield_10145 → Environment  (mandatory)
   */
  buildInstanceAccessPayload(requestDetails) {
    const userEmail = requestDetails.userEmail || requestDetails.email;
    const emailParts = userEmail ? userEmail.split('@')[0].split('.') : ['User', 'Name'];
    const firstName = requestDetails.firstName || (emailParts[0] ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1) : 'User');
    const lastName = requestDetails.lastName || (emailParts[1] ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1) : '');
    const fullName = `${firstName} ${lastName}`.trim();
    const instanceName = requestDetails.instanceName || requestDetails.selectedInstance || requestDetails.instanceUrl || 'Unknown Instance';
    const reason = requestDetails.reason || requestDetails.reasonForAccess || 'Not specified';
    const environment = this.isProduction ? 'Production' : 'Development';

    return {
      serviceDeskId: '2',
      requestTypeId: '12',
      requestFieldValues: {
        summary: `Instance Access Request - ${fullName} (${userEmail})`,
        description: `Instance access request submitted by ${fullName} for ${instanceName}.`,
        priority: { name: 'P3' },
        customfield_16130: fullName,
        customfield_16131: userEmail,
        customfield_16132: requestDetails.companyName || 'Not specified',
        customfield_16135: 'Review and approve instance access request',
        customfield_16136: instanceName,
        customfield_16137: reason,
        customfield_10145: { value: environment }
      }
    };
  }

  /**
   * Test ticket creation
   */
  async testNotification() {
    return this.sendAccountCreationNotification({
      firstName: 'Test',
      lastName: 'User',
      email: 'vadiraj.joshi@laerdal.com',
      companyName: 'Laerdal',
      primaryUseCase: 'Testing',
      reasonForUse: 'Testing JIRA service desk ticket creation',
      submittedAt: new Date().toLocaleString()
    });
  }
}

export const jiraNotificationService = new JiraNotificationService();
export default JiraNotificationService;
